import { useEffect, useRef, useState, type JSX } from "react";
import type { components } from "../../api/schema";
import {
  listCharacterArcHistory,
  type CharacterArcHistoryEntry,
} from "../../api/character-arc-history";

export type UnlockedCharacter = components["schemas"]["UnlockedCharacter"];

export type CharacterCodexEntryProps = {
  character: UnlockedCharacter;
  // character-arc-history Phase 3: arc timeline 表示用 optional context。
  // scenarioId + apiBaseUrl 両方渡されたとき「成長の軌跡」 collapsible section を表示、
  // 不在経路では既存挙動 bit-by-bit 互換 (= section 完全 omit)。
  scenarioId?: string;
  apiBaseUrl?: string;
};

/**
 * 1 キャラ entry の表示。level に応じて公開フィールドを段階的に出し分ける。
 *
 * - level 1: name / role のみ
 * - level 2: + want / ghost
 * - level 3: + need / flaw / truth + 関係性 textリスト
 *
 * portrait が設定済のとき、ヘッダー左にアバターを表示する。
 * アバタータップ → portrait 拡大ビュー（詳細に「近づく」UX）。
 */
export function CharacterCodexEntry({
  character,
  scenarioId,
  apiBaseUrl,
}: CharacterCodexEntryProps): JSX.Element {
  const { characterId, name, role, level, portraitUrl } = character;

  // ポートレート拡大ビュー管理
  const [portraitExpanded, setPortraitExpanded] = useState(false);
  const portraitBtnRef = useRef<HTMLButtonElement>(null);

  // character-arc-history Phase 3: 「成長の軌跡」 timeline の collapsible state + lazy fetch。
  // expand 時に初回 fetch、 collapse 後再 expand では cached を再利用 (= 既に取得済なら refetch
  // skip)。 arc text + scenarioId + apiBaseUrl 全 valid + level >= 3 のときのみ active。
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyEntries, setHistoryEntries] = useState<
    CharacterArcHistoryEntry[] | null
  >(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const historyEnabled =
    level >= 3 &&
    character.arc !== undefined &&
    character.arc !== "" &&
    scenarioId !== undefined &&
    apiBaseUrl !== undefined;
  useEffect(() => {
    if (!historyOpen) return;
    if (historyEntries !== null) return; // already cached
    if (!historyEnabled) return;
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError(null);
    void listCharacterArcHistory(apiBaseUrl!, scenarioId!, characterId)
      .then((res) => {
        if (cancelled) return;
        setHistoryEntries(res.entries);
        setHistoryLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setHistoryError(err instanceof Error ? err.message : String(err));
        setHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    historyOpen,
    historyEntries,
    historyEnabled,
    apiBaseUrl,
    scenarioId,
    characterId,
  ]);

  return (
    <div
      data-testid={`character-codex-entry-${characterId}`}
      className="px-4 py-3 bg-reader-surface rounded-lg text-reader-primary space-y-1"
    >
      {/* ポートレート拡大ビュー：タップ後に entry 上部いっぱいに広がり「詳細へ近づく」演出 */}
      {portraitUrl !== null && portraitUrl !== undefined && portraitExpanded && (
        <div
          data-testid={`character-codex-entry-${characterId}-portrait-expanded`}
          className="relative -mx-4 -mt-3 mb-3 overflow-hidden rounded-t-lg"
          style={{ maxHeight: "260px" }}
        >
          <img
            src={portraitUrl}
            alt={name}
            className="w-full object-cover object-top"
            style={{ animation: "fadeIn 0.3s ease-out" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-reader-surface" />
          <button
            type="button"
            onClick={() => {
              setPortraitExpanded(false);
              portraitBtnRef.current?.focus();
            }}
            className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-black/40 text-white text-xs hover:bg-black/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
            aria-label="ポートレートを閉じる"
          >
            ✕
          </button>
        </div>
      )}

      {/* ヘッダー行: アバター + 名前/役割 */}
      <div className="flex items-center gap-3">
        {portraitUrl !== null && portraitUrl !== undefined ? (
          <button
            ref={portraitBtnRef}
            type="button"
            onClick={() => setPortraitExpanded((v) => !v)}
            aria-label={`${name}のポートレートを拡大`}
            aria-expanded={portraitExpanded}
            className="shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
          >
            <img
              src={portraitUrl}
              alt={name}
              className="w-11 h-11 rounded-full object-cover object-top"
              style={{ transition: "transform 0.2s ease", transform: portraitExpanded ? "scale(0.9)" : "scale(1)" }}
            />
          </button>
        ) : (
          <div
            className="shrink-0 w-11 h-11 rounded-full bg-reader-secondary flex items-center justify-center text-base font-bold text-white"
            aria-hidden="true"
          >
            {name.charAt(0)}
          </div>
        )}
        <div className="text-base font-semibold">
          {name} / {role}
        </div>
      </div>

      {level >= 2 && (
        <>
          {character.want !== undefined && (
            <div className="text-sm text-reader-secondary">want: {character.want}</div>
          )}
          {character.ghost !== undefined && (
            <div className="text-sm text-reader-secondary">
              ghost: {character.ghost}
            </div>
          )}
        </>
      )}

      {level >= 3 && (
        <>
          {character.need !== undefined && (
            <div className="text-sm text-reader-secondary">need: {character.need}</div>
          )}
          {character.flaw !== undefined && (
            <div className="text-sm text-reader-secondary">flaw: {character.flaw}</div>
          )}
          {character.truth !== undefined && (
            <div className="text-sm text-reader-secondary">
              truth: {character.truth}
            </div>
          )}
          {character.relationships !== undefined &&
            character.relationships.length > 0 && (
              <div className="text-sm text-reader-secondary">
                <div className="font-medium">関係性:</div>
                <ul className="list-disc pl-5 space-y-0.5">
                  {character.relationships.map((rel, idx) => {
                    // intensity が正値のときは + を付与して符号を明示する。
                    const intensityLabel =
                      rel.intensity > 0
                        ? `+${rel.intensity}`
                        : `${rel.intensity}`;
                    return (
                      <li key={`${rel.targetCharName}-${idx}`}>
                        {rel.targetCharName} ({rel.type}, {intensityLabel})
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          {/* relationship-axis-registry (2026-06-01 reader codex 可視化): creator 定義軸タグの表示。
              level 3 のみ公開、 軸タグなし / 未配備で section omit (= backward compat)。 */}
          {character.axes !== undefined && character.axes.length > 0 && (
            <div
              data-testid={`character-codex-entry-${characterId}-axes`}
              className="text-sm text-reader-secondary"
            >
              <div className="font-medium">関係性軸:</div>
              <ul className="list-disc pl-5 space-y-0.5">
                {character.axes.map((axis, idx) => (
                  <li key={`${axis.targetCharName}-${axis.axisName}-${idx}`}>
                    {axis.targetCharName}: {axis.axisName} Lv{axis.level}「{axis.levelLabel}」
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* reader-character-arc-codex (機構 M14 reader 側 surface): 主人公 character のみ
              arc text 表示 (= 既存 truth / need / flaw / relationships と同 level 3 layer)。
              arc undefined / 空文字 で section 完全 omit (= backward compat、 第 1+2 段未配備
              / 主人公 不在 / arc 未蓄積で既存 codex 経路と bit-by-bit 互換)。 */}
          {character.arc !== undefined && character.arc !== "" && (
            <div
              data-testid={`character-codex-entry-${characterId}-arc`}
              className="text-sm text-reader-secondary"
            >
              <div className="font-medium">成長:</div>
              <p className="whitespace-pre-wrap pl-2">{character.arc}</p>
              {/* character-arc-history Phase 3: 「成長の軌跡」 timeline collapsible section。
                  scenarioId + apiBaseUrl 渡された level 3 codex でのみ active、 不在経路では
                  既存挙動 bit-by-bit 互換 (= section 完全 omit)。 */}
              {historyEnabled && (
                <div
                  data-testid={`character-codex-entry-${characterId}-arc-history`}
                  className="mt-2 pl-2"
                >
                  <button
                    type="button"
                    onClick={() => setHistoryOpen(!historyOpen)}
                    aria-expanded={historyOpen}
                    aria-controls={`arc-history-list-${characterId}`}
                    className="text-xs text-reader-secondary underline hover:text-reader-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
                  >
                    {historyOpen ? "成長の軌跡を閉じる" : "成長の軌跡を表示"}
                  </button>
                  {historyOpen && (
                    <div
                      id={`arc-history-list-${characterId}`}
                      className="mt-1"
                      role="region"
                      aria-label="成長の軌跡 timeline"
                    >
                      {historyLoading && (
                        <p className="text-xs text-reader-secondary">
                          読込中…
                        </p>
                      )}
                      {historyError !== null && (
                        <p
                          className="text-xs text-reader-secondary"
                          role="alert"
                        >
                          履歴の取得に失敗しました
                        </p>
                      )}
                      {historyEntries !== null && historyEntries.length === 0 && (
                        <p className="text-xs text-reader-secondary">
                          まだ記録された変化はありません
                        </p>
                      )}
                      {historyEntries !== null && historyEntries.length > 0 && (
                        <ul className="space-y-1 list-disc pl-5">
                          {historyEntries.map((entry) => (
                            <li
                              key={entry.id}
                              className="text-xs text-reader-secondary"
                              data-testid={`arc-history-entry-${entry.id}`}
                            >
                              <time dateTime={entry.transitionedAt}>
                                {new Date(entry.transitionedAt).toLocaleString(
                                  "ja-JP",
                                )}
                              </time>
                              <p className="whitespace-pre-wrap pl-1">
                                {entry.toArcText}
                              </p>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
